import {
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  Renderer2,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { formatPhone, normalizePhone } from '../utils/phone-utils';

/**
 * Phone input mask directive.
 *
 * Displays formatted phone (XXX)-XXX-XXXX in the input while storing
 * only the raw 10-digit value in the form control / ngModel.
 *
 * Works with both Reactive Forms and Template-driven Forms (ngModel).
 *
 * @example
 *   <input appPhoneInputMask formControlName="phone" />
 *   <input appPhoneInputMask [(ngModel)]="parent.phone" />
 */
@Directive({
  selector: '[appPhoneInputMask]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputMaskDirective),
      multi: true,
    },
  ],
})
export class PhoneInputMaskDirective implements ControlValueAccessor {
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private el = inject<ElementRef<HTMLInputElement>>(ElementRef);
  private renderer = inject(Renderer2);

  /** Write a new value from the form model into the view. */
  writeValue(value: string | null): void {
    const formatted = formatPhone(value);
    this.renderer.setProperty(this.el.nativeElement, 'value', formatted);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled);
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value;

    // Extract only digits, limit to 10
    const digits = rawValue.replace(/\D/g, '').slice(0, 10);

    // Format for display
    const formatted = this.formatPartial(digits);
    this.renderer.setProperty(this.el.nativeElement, 'value', formatted);

    // Emit normalized value (only digits) to the form control
    this.onChange(digits);
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') || '';
    const digits = normalizePhone(pasted).slice(0, 10);
    const formatted = this.formatPartial(digits);
    this.renderer.setProperty(this.el.nativeElement, 'value', formatted);
    this.onChange(digits);
  }

  /**
   * Formats digits progressively as the user types.
   *   1-3 digits:  (XXX
   *   4-6 digits:  (XXX)-XXX
   *   7-10 digits: (XXX)-XXX-XXXX
   */
  private formatPartial(digits: string): string {
    if (!digits) return '';

    const len = digits.length;

    if (len <= 3) {
      return `(${digits}`;
    }
    if (len <= 6) {
      return `(${digits.substring(0, 3)})-${digits.substring(3)}`;
    }
    // Last 4 digits together without dash
    return `(${digits.substring(0, 3)})-${digits.substring(3, 6)}-${digits.substring(6)}`;
  }
}
